import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ShoppingBag, Tag, Crown, Loader2 } from 'lucide-react';
import { useAuth, api } from '../context/AuthContext';
import { toast } from 'sonner';

const Merch = () => {
  const { currentUser } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await api.get('/api/merch/items');
        setItems(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Failed to load merch', e);
        toast.error('Failed to load merch');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const categories = [
    { id: 'all', name: 'All Items' },
    { id: 'apparel', name: 'Apparel' },
    { id: 'products', name: 'Products' },
    { id: 'gift', name: 'Gift Cards' },
    { id: 'other', name: 'Other' },
  ];

  const filteredItems = selectedCategory === 'all'
    ? items
    : items.filter(item => item.category === selectedCategory);

  const hasMemberDiscount = ['basic', 'vip', 'gentleman'].includes(
    (currentUser?.membership_tier || currentUser?.membershipTier || '').toLowerCase()
  );

  const getMemberDiscount = (price) => {
    if (hasMemberDiscount) return (price * 0.9).toFixed(2); // 10% off
    return price.toFixed(2);
  };

  const handleOrderClick = (item) => {
    toast.info('Available in-shop', {
      description: `Call (239) 201-8105 or visit us to grab "${item.name}"`,
    });
  };

  return (
    <div className="min-h-screen bg-black text-white py-16" data-testid="merch-page">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <ShoppingBag className="w-16 h-16 text-lime-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-lime-400 to-lime-600">
              Sauce Sity Merch
            </span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Rep the brand with official Sauce Sity merchandise and premium barber products
          </p>

          {hasMemberDiscount && (
            <div className="mt-6 inline-block">
              <Badge className="bg-purple-600 text-white px-4 py-2 text-base">
                <Crown className="w-4 h-4 mr-2 inline" />
                Member Perk: 10% Off All Merch
              </Badge>
            </div>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-16">
            <Loader2 className="w-12 h-12 animate-spin text-lime-400" />
          </div>
        )}

        {/* Empty state */}
        {!loading && items.length === 0 && (
          <Card className="bg-zinc-900 border-zinc-800 max-w-xl mx-auto" data-testid="merch-empty">
            <CardContent className="p-10 text-center">
              <ShoppingBag className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Coming Soon</h3>
              <p className="text-zinc-400">
                New merch is dropping. Check back soon or call us at{' '}
                <a href="tel:2392018105" className="text-lime-400">(239) 201-8105</a>.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Category filter (only shown when there's at least one item) */}
        {!loading && items.length > 0 && (
          <div className="flex justify-center gap-3 mb-12 flex-wrap">
            {categories.map(cat => (
              <Button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                variant={selectedCategory === cat.id ? 'default' : 'outline'}
                className={selectedCategory === cat.id
                  ? 'bg-lime-400 text-black hover:bg-lime-500'
                  : 'border-zinc-700 text-white hover:bg-zinc-800'
                }
                data-testid={`merch-cat-${cat.id}`}
              >
                {cat.name}
              </Button>
            ))}
          </div>
        )}

        {/* Products Grid */}
        {!loading && filteredItems.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-testid="merch-grid">
            {filteredItems.map(item => (
              <Card key={item.id} className="bg-zinc-900 border-zinc-800 overflow-hidden" data-testid={`merch-item-${item.id}`}>
                <div className="relative h-64 overflow-hidden bg-zinc-800 flex items-center justify-center">
                  {item.image_url ? (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-full h-full object-contain hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-700">
                      <ShoppingBag className="w-20 h-20" />
                    </div>
                  )}
                  {item.in_stock === false && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                      <Badge className="bg-red-600 text-white text-lg px-4 py-2">
                        Out of Stock
                      </Badge>
                    </div>
                  )}
                </div>

                <CardHeader>
                  <CardTitle className="text-xl text-white">{item.name}</CardTitle>
                  {item.description && (
                    <p className="text-zinc-400 text-sm">{item.description}</p>
                  )}
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="flex items-baseline gap-2">
                    {hasMemberDiscount ? (
                      <>
                        <span className="text-lime-400 text-2xl font-bold">
                          ${getMemberDiscount(item.price)}
                        </span>
                        <span className="text-zinc-500 line-through text-lg">
                          ${item.price.toFixed(2)}
                        </span>
                        <Badge className="bg-purple-600 text-xs">10% off</Badge>
                      </>
                    ) : (
                      <span className="text-lime-400 text-2xl font-bold">
                        ${item.price.toFixed(2)}
                      </span>
                    )}
                  </div>

                  {item.sizes && item.sizes.length > 0 && (
                    <div>
                      <p className="text-zinc-500 text-sm mb-2">Available: {item.sizes.join(', ')}</p>
                    </div>
                  )}

                  <Button
                    onClick={() => handleOrderClick(item)}
                    disabled={item.in_stock === false}
                    className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800 disabled:opacity-50"
                    data-testid={`merch-order-${item.id}`}
                  >
                    <Tag className="w-4 h-4 mr-2" />
                    Inquire
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info Box */}
        <Card className="bg-gradient-to-r from-purple-900/40 to-black border-purple-500 max-w-3xl mx-auto">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold text-lime-400 mb-4">How to Order</h3>
            <p className="text-zinc-300 mb-6">
              Call us at <a href="tel:2392018105" className="text-lime-400 font-semibold">(239) 201-8105</a> or
              visit the shop to purchase merchandise. We accept cash and card payments.
            </p>
            <p className="text-zinc-500 text-sm">
              📍 2401 Hancock Bridge Pkwy Unit 2, Cape Coral, FL 33990
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Merch;
